import React from 'react';
import { Container } from '../ui/Container';
import { useIsMobile } from '../../Hooks/useIsMobile'; // Assuming path
import { getLowestPricedEdition, getOfferPrice, PACKAGES } from '../../Constants/Packages';
import { Button } from '../ui/Button';
import { FLASHOFFER } from '../../Constants/Const';
import { getActiveOffer } from '../../Utils/OfferEngine';
import { useBrand } from '../../Utils/BrandContext';


const FeaturesSection = ({ onBuyNow, pricing = { silver: 2999 }, rfdAvl, onBuyNowClick, isFromIdx = false, pageName = "" }) => {
  const isMobile = useIsMobile();
  const { brand: activeBrand } = useBrand()
  const activeEdition = pageName?.toLowerCase() === 'gqik' ? getLowestPricedEdition("GQIK") : getLowestPricedEdition("GPOWER");
  const activeOffer = getActiveOffer();
  const flashOfferOn = activeOffer?.offerKey === 'FLASH_EOD';
  const hasAnyOffer = !!activeOffer && activeOffer?.offerKey !== 'FRESH';
  const offerPrice = getOfferPrice(activeEdition, activeOffer);

  const scrollToSection = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const mainFeatures = [
    'CRM', 'Quotes', 'Orders',
    'Billing', 'E-Way Bill', 'E-Invoice',
    'Accounts', 'GSTR1 & 3B', 'Recovery',
    'Inventory', 'Batches', 'BOM',
    'Purchase Orders', 'Suppliers Invoices',
    'MRP', 'Projects', 'Manufacturing',
    'Tasks', 'Support Ticketing', 'Contracts'
  ];
  const featuresWithIcons = [
    { name: 'CRM', icon: 'fa fa-users' },
    { name: 'Quotes', icon: 'fas fa-file-alt' },
    { name: 'Orders', icon: 'fas fa-box' },
    { name: 'Billing', icon: 'fas fa-money-bill-wave' },
    { name: 'E-Way Bill', icon: 'fas fa-truck' },
    { name: 'E-Invoice', icon: 'fas fa-file-invoice' },
    { name: 'Accounts', icon: 'fas fa-inr' },
    { name: 'GSTR1 & 3B', icon: 'fas fa-landmark' },
    { name: 'Recovery', icon: 'fas fa-hand-holding-usd' },
    { name: 'Inventory', icon: 'fas fa-warehouse' },
    { name: 'Batches', icon: 'fas fa-layer-group' },
    { name: 'BOM', icon: 'fas fa-clipboard-list' },
    { name: 'Purchase Orders', icon: 'fa fa-shopping-cart' },
    { name: 'Suppliers Invoices', icon: 'fas fa-user-tie' },
    { name: 'MRP', icon: 'fas fa-calculator' },
    { name: 'Projects', icon: 'fas fa-project-diagram' },
    { name: 'Manufacturing', icon: 'fas fa-industry' },
    { name: 'Tasks', icon: 'fas fa-tasks' },
    { name: 'Support Ticketing', icon: 'fas fa-headset' },
    { name: 'Contracts', icon: 'fas fa-file-contract' },
  ];
  const renderFeatureRow = (startIndex) => {
    const features = mainFeatures.slice(startIndex, startIndex + 3);
    return (
      <div key={startIndex} className="text-center leading-[30px]">
        {features.map((feature, idx) => (
          <span key={idx} className="inline-block mx-0">
            <span className="text-primary font-bold mr-1">✓</span>
            <span className="text-sm md:text-base text-text-primary mr-2">{feature}</span>
          </span>
        ))}
      </div>
    );
  };

  // Shared Card Wrapper class for consistency
  const cardClass = `bg-background-light rounded border border-border shadow-light hover:shadow-medium transition-all overflow-hidden`;

  return (
    <section id="featuresSection" className={` ${!isMobile ? 'by-white py-12 border-top md:py-16 lg:py-20' : 'bg-background-light pb-12 md:pb-16 lg:pb-20'}`}>
      <Container >
        {/* Desktop Layout */}
        <div className="hidden lg:block">
          {/* 1. Powerful Features - Full Width with Icons Grid */}
          <div className={`${cardClass} cursor-pointer`} onClick={() => scrollToSection('productfeaturessection')}>
            <div className="bg-background-light pb-4 pt-6 md:py-[25px] lg:py-4">
              <h3 className="text-[22px] md:text-[28px] font-bold secttl text-center m-0 px-4">
                Powerful Features
              </h3>
            </div>
            <div className="px-8 pb-8 pt-4">
              {/* Grid of features with icons */}
              <div className="grid grid-cols-5 gap-x-6 gap-y-4">
                {featuresWithIcons.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <i className={`${feature.icon} flex-shrink-0 text-primary`} style={{ fontSize: '16px', lineHeight: '1.25' }}></i>
                    <span className="text-text-primary font-bold text-base leading-tight">{feature.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* @ Separator */}
          <div className="flex items-center justify-center my-4">
            <span className="text-[40px] font-bold text-primary">@</span>
          </div>

          {/* Bottom Row - 3 Cards */}
          <div className="flex items-stretch justify-center gap-4" onClick={() => scrollToSection('simplePricing')}>
            {/* Truly Lowest Price */}
            <div className={`${cardClass} ${rfdAvl ? 'flex-1' : 'w-[320px]'} flex flex-col cursor-pointer`} onClick={() => scrollToSection('pricingSection')}>
              <div className="bg-background-light pb-4 pt-6 md:py-[25px] lg:py-4">
                <h3 className="text-[22px] md:text-[28px] font-bold secttl text-center m-0 px-4">
                  Truly Lowest Price
                </h3>
              </div>
              <div className="px-4 pb-6 pt-0 text-center">
                <div className="mb-3">
                  <div >
                    <span className="text-[11px] text-text-secondary">₹ </span><span className="text-base text-text-secondary line-through">{offerPrice.stdPrice}</span>
                  </div>
                  <div>
                    <span className="text-base text-text-secondary">₹</span>
                    <span className="text-[32px] lg:text-[40px] font-bold text-primary mx-1">
                      {offerPrice.displayPrice}
                    </span>
                    <span className="text-base text-text-secondary">/ year +GST</span>
                  </div>
                </div>
                <div className="text-base text-text-primary leading-normal">
                  <p className="m-1.5">No per-user/hidden charges</p>
                </div>
              </div>
            </div>

            {/* & Separator */}
            <div className="flex items-center justify-center px-2">
              <span className="text-[40px] font-bold text-primary">&</span>
            </div>

            {/* Best Support */}
            <div className={`${cardClass} ${rfdAvl ? 'flex-1' : 'w-[320px]'} flex flex-col cursor-pointer`} onClick={() => scrollToSection('premium-support')}>
              <div className="bg-background-light pb-4 pt-6 md:py-[25px] lg:py-4">
                <h3 className="text-[22px] md:text-[28px] font-bold secttl text-center m-0 px-4">
                  Best Support
                </h3>
              </div>
              <div className="px-4 pb-6 pt-0 text-center flex flex-col justify-center flex-1">
                <p className="m-1 text-base text-text-primary">Available even on Sundays</p>
                <p className="m-1 text-base text-text-primary">Live Training</p>
              </div>
            </div>

            {rfdAvl && <>
              <div className="flex items-center justify-center px-2">
                <span className="text-[40px] font-bold text-primary">@</span>
              </div>
              <div className={`${cardClass} flex-1 flex flex-col`}>
                <div className="bg-background-light pb-4 pt-6 md:py-[25px] lg:py-4">
                  <h3 className="text-[22px] md:text-[28px] font-bold text-primary text-center m-0 px-4">
                    Zero Risk
                  </h3>
                </div>
                <div className="px-4 pb-6 pt-0 text-center flex flex-col justify-center flex-1">
                  <p className="m-1 text-base text-text-primary">40 years of Spotless reputation</p>
                  <p className="m-1 text-base text-text-primary">7-day money-back guarantee</p>
                </div>
              </div>
            </>}
          </div>
        </div>

        {/* Mobile Layout - Redesigned */}
        <div className="lg:hidden">
          <div className={`flex flex-col items-center gap-0 ${isFromIdx ? 'pt-6' : ''}`}>

            {/* Powerful Features - No Card */}
            <div className="text-center cursor-pointer" onClick={() => scrollToSection('productfeaturessection')}>
              <h3 className="text-[25px] md:text-[40px] font-bold secttl m-0">Powerful Features</h3>
            </div>

            {/* Plus Separator */}
            <div className="flex items-center justify-center my-1">
              <span className="text-[35px] md:text-[40px] font-bold text-primary">+</span>
            </div>

            {/* Strong Support - No Card */}
            <div className="text-center cursor-pointer" onClick={() => scrollToSection('premium-support')}>
              <h3 className="text-[25px] md:text-[40px] font-bold secttl m-0">Strong Support</h3>
            </div>

            {/* @ Separator */}
            <div className="flex items-center justify-center my-1">
              <span className="text-[30px] font-bold text-primary">@</span>
            </div>

            {/* Unbeatable Prices - No Card */}
            <div className="text-center cursor-pointer mb-3" onClick={() => scrollToSection('simplePricing')}>
              <h3 className="text-[25px] md:text-[40px] font-bold secttl m-0">Unbeatable Price</h3>
            </div>

            {/* Pricing Card with Unbeatable Prices inside */}
            <div className={` w-full max-w-[270px] mx-auto cursor-pointer`} onClick={() => scrollToSection('simplePricing')}>
              <div className="px-4 py-4 text-center">
                {(() => {
                  const pkgId = pageName?.toLowerCase() === 'gpower' ? 36 : 34;
                  const pkg = PACKAGES[pkgId];
                  const stdAmt = pkg?.stdAmtStr || pkg?.stdAmt;
                  const baseAmt = pkg?.baseAmtStr || pkg?.baseAmt;
                  return (
                    <div>
                      <div>
                        <span className="text-base text-text-secondary">Just ₹ </span>
                        <span className="text-base text-text-secondary line-through">{stdAmt}</span>
                      </div>
                      <div>
                        <span className="text-[22px] text-text-secondary">₹</span>
                        <span className="text-[50px] font-bold text-primary mx-1">
                          {baseAmt}
                        </span>
                      </div>
                      <span className="text-base text-text-secondary">per year +GST</span>
                    </div>
                  );
                })()}
              </div>
              <div className="mx-4 pb-4 text-center">
                {hasAnyOffer && (
                  <div
                    className="mt-3 rounded border border-primary bg-primary-subtle cursor-pointer px-4 py-3 flex flex-col gap-2"
                    onClick={onBuyNow}
                  >
                    <p className="m-0 font-bold text-primary text-[22px]">{activeOffer?.label || 'Special Offer'}</p>
                    <p className="m-0 text-text-primary leading-normal">Get Extra Discount!</p>
                    <p className="m-0 text-text-primary mt-1">Start First year @ just</p>
                    <div className="flex flex-col items-center gap-0">
                      <div>
                        <span className="text-text-secondary">₹ </span>
                        <span className="text-text-secondary line-through">{offerPrice.stdPrice}</span>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-[20px] text-text-secondary">₹</span>
                        <span className="text-[40px] font-bold text-primary">{offerPrice.displayPrice}</span>
                        <span className="text-xs text-text-secondary">+GST</span>
                      </div>
                    </div>
                    <style>{`@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }`}</style>
                    <Button
                      onClick={(e) => { e.stopPropagation(); onBuyNowClick?.(); onBuyNow?.(); }}
                    >
                      Buy Now
                    </Button>
                  </div>
                )}
                {!hasAnyOffer && <Button
                  onClick={(e) => { e.stopPropagation(); onBuyNowClick?.(); onBuyNow?.(); }}
                  className="w-full"

                >
                  Buy Now
                </Button>}
              </div>
            </div>

            {rfdAvl && <>
              <div className="flex items-center justify-center my-3">
                <span className="text-[30px] font-bold text-primary">@</span>
              </div>
              <div className={`${cardClass} w-full bg-slate-50`}>
                <div className="bg-transparent pb-4 pt-6">
                  <h3 className="text-[22px] font-bold text-primary text-center m-0 px-4">
                    Zero Risk
                  </h3>
                </div>
                <div className="px-4 pb-6 pt-0 text-center flex flex-col justify-center flex-1">
                  <p className="m-1 text-base text-text-primary">40 years of Spotless reputation</p>
                  <p className="m-1 text-base text-text-primary">7-day money-back guarantee</p>
                </div>
              </div>
            </>}
          </div>
        </div>

        {/* Desktop Flash Offer Card - above Buy Now */}
        {flashOfferOn && (
          <div className="hidden lg:block mt-12">
            <div className="border-2 border-primary bg-primary-subtle rounded p-4 max-w-2xl mx-auto">
              <div className="flex items-end justify-between gap-6">
                <div>
                  <p className="m-0 font-bold text-primary text-[22px] mb-1">Flash Offer</p>
                  <p className=" font-semibold text-text-primary mb-1">{FLASHOFFER.tagline}</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-[11px] text-text-secondary">₹</span>
                    <span className="text-sm font-bold text-text-secondary line-through">{offerPrice.stdPrice}</span>
                    <span className="text-[18px] text-text-secondary ml-1">₹</span>
                    <span className="text-[28px] font-extrabold text-text-primary">{offerPrice.displayPrice}</span>
                    <span className="text-[15px] text-text-secondary">+GST</span>
                  </div>
                </div>
                <div className="flex items-center">
                  <Button
                    size="lg"
                    onClick={(e) => { e.stopPropagation(); onBuyNow?.(); }}
                  >
                    Get Offer
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Buy Now Button - Desktop only */}
        {!flashOfferOn && (
          <div className="hidden lg:block text-center mt-6">
            <Button
              onClick={() => { onBuyNowClick?.(); onBuyNow?.(); }}
              size="lg"
            >
              Buy Now
            </Button>
          </div>
        )}
      </Container>
    </section>
  );
};

export default FeaturesSection;